import React, { useEffect, useState } from "react";

// components
import SelectInput from "../../../../../../components/Inputs/SelectInput";
import Text from "../../../../../../components/Text";

// api hooks
import { useCohorts, usePageSettings, useUpdatePageSettings } from "../../../../../../api/hooks";

// config
import Config from "../../../../Config";
// styles
import css from "./CalendarFactorsHeatmap.module.scss";

const { DEFAULT_YEAR_SELECTION, DEFAULT_COHORT_SELECTION } = Config.FACTORS.HEATMAP;

interface BaseProps {
	years: number[];
}

/**
 * Selections for year and cohort to filter the heatmap by.
 * Updates the persistent page settings in db to store the selections.
 * Parent component checks page settings to determine which data to display
 */
const YearAndCohortDropdowns = ({ years }: BaseProps) => {
	const [year, setSelectedYear] = useState<string>(DEFAULT_YEAR_SELECTION.toString());
	const [cohort, setSelectedCohort] = useState<string>(DEFAULT_COHORT_SELECTION);

	// API hooks
	const { isLoading: isCohortsLoading, data: cohortsData } = useCohorts();

	// current selections if stored in page settings, otherwise defaults
	const pageSettingsOnSuccess = (onSuccessData) => {
		const newSelectedCohort = onSuccessData?.factors?.heatmap?.cohort;
		const newSelectedYear = onSuccessData?.factors?.heatmap?.year;
		const cohortExists = cohortsData?.cohorts.find((c) => c._id === newSelectedCohort);
		if (newSelectedCohort && newSelectedCohort !== cohort && cohortExists) {
			setSelectedCohort(newSelectedCohort);
		} else {
			setSelectedCohort(DEFAULT_COHORT_SELECTION);
		}
		if (newSelectedYear && newSelectedYear !== year && years.includes(parseInt(year))) {
			setSelectedYear(newSelectedYear);
		} else {
			setSelectedYear(DEFAULT_YEAR_SELECTION.toString());
		}
	};

	const updatePageSettings = useUpdatePageSettings(pageSettingsOnSuccess).mutate;

	// state for selected cohorts
	const {
		data: pageSettingsData,
		refetch: refetchPageSettings,
		isLoading: isPageSettingsLoading,
		isSuccess,
	} = usePageSettings();

	useEffect(() => {
		if (!isPageSettingsLoading && isSuccess) {
			pageSettingsOnSuccess(pageSettingsData);
		}
	}, [isPageSettingsLoading, isSuccess]);

	// refetch page settings when cohorts have loaded
	useEffect(() => {
		if (!isCohortsLoading) {
			refetchPageSettings();
		}
	}, [isCohortsLoading]);

	const yearsOptions = years.map((yr) => ({ label: yr.toString(), value: yr.toString() }));
	const cohortsOptions = [
		{ label: "All Employees", value: "all" },
		...(cohortsData?.cohorts
			.sort((coA, coB) => (coA.name.trim().toLowerCase() > coB.name.trim().toLowerCase() ? 1 : -1))
			.map((cohort) => ({ label: cohort.name, value: cohort._id })) || []),
	];

	// update page settings if selected cohort are not in cohortsData
	useEffect(() => {
		if (cohortsData && cohort !== "all" && !cohortsData.cohorts.find((c) => c._id === cohort)) {
			updatePageSettings({
				settings_path: "factors.heatmap.cohort",
				settings_value: "all",
			});
		}
	}, [cohort, cohortsData]);

	// update page settings if selected year is not in years
	useEffect(() => {
		if (!years.includes(parseInt(year))) {
			updatePageSettings({
				settings_path: "factors.heatmap.year",
				settings_value: DEFAULT_YEAR_SELECTION.toString(),
			});
		}
	}, [year, years]);

	return (
		<div className={css.dropdowns}>
			<div className={css.labeledInput}>
				<label htmlFor="year-filter">
					<Text size="caption" weight="semi-bold">
						Filter by Year
					</Text>
				</label>
				<div className={css.select}>
					<SelectInput
						size="small"
						isSearchable={false}
						inputId="year-filter"
						name="Year"
						results={yearsOptions}
						value={year}
						onChange={({ value }) => {
							updatePageSettings({ settings_path: "factors.heatmap.year", settings_value: value });
						}}
					/>
				</div>
			</div>
			<div className={css.labeledInput}>
				<label htmlFor="cohort-filter">
					<Text size="caption" weight="semi-bold">
						Filter by Cohort
					</Text>
				</label>
				<div className={css.select}>
					<SelectInput
						size="small"
						inputId="cohort-filter"
						name="Cohort"
						// by portalling to doc.body, allows title text to only show on hover for the control input, not the menu itself
						menuPortalTarget={document.querySelector("body")}
						results={cohortsOptions}
						value={cohort}
						onChange={({ value }) => {
							// if the selection is for "All Employees", the value used is null (prevents looking up data for cohort with id: "all")
							const settings_value = value === "all" ? null : value;
							updatePageSettings({
								settings_path: "factors.heatmap.cohort",
								settings_value,
							});
						}}
					/>
				</div>
			</div>
		</div>
	);
};

export default YearAndCohortDropdowns;
